import { Link } from 'react-router-dom'
import { useMemo, useState } from 'react'
import { Search, ArrowRight } from 'lucide-react'
import { Badge, Button } from '@/components/ui'
import { getBodyTypes, getStatesWithCities } from '@/data/shop'
import { useSeo } from '@/lib/seo'

function CardImage({
  webpSrc,
  jpgSrc,
  fallback,
}: {
  webpSrc: string
  jpgSrc: string
  fallback: string
}) {
  const [failed, setFailed] = useState(false)
  if (failed) {
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-6xl font-headline font-light text-navy-900/10 group-hover:scale-110 transition-transform duration-500">
          {fallback}
        </span>
      </div>
    )
  }
  return (
    <picture>
      <source srcSet={webpSrc} type="image/webp" />
      <img
        src={jpgSrc}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
      />
    </picture>
  )
}

export function ShopPage() {
  const bodyTypes = getBodyTypes()
  const statesWithCities = getStatesWithCities()

  useSeo({
    title: 'Shop | Bespokian Auto',
    description: 'Explore collections by body type and shop by state.',
    image: '/img/og/home-og.jpg',
  })

  const [query, setQuery] = useState('')
  const q = query.trim().toLowerCase()

  const filteredBodyTypes = useMemo(() => {
    if (!q) return bodyTypes
    return bodyTypes.filter((bt) => bt.name.toLowerCase().includes(q) || bt.slug.includes(q))
  }, [bodyTypes, q])

  const filteredStates = useMemo(() => {
    if (!q) return statesWithCities
    return statesWithCities.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.abbreviation.toLowerCase().includes(q) ||
        s.slug.includes(q),
    )
  }, [statesWithCities, q])

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - immersive style (match BrandsPage) */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('/img/heroes/home-hero-hero.webp')` }}
        />
        <div className="absolute inset-0 bg-black/35" />

        <div className="relative z-10 w-full flex flex-col items-center justify-center px-6">
          <div className="text-center max-w-4xl w-full">
            <h1 className="font-display text-5xl md:text-6xl text-white leading-tight tracking-tight">
              Shop
            </h1>
          </div>

          <div className="mt-10 w-full max-w-2xl">
            <form className="w-full" onSubmit={(e) => e.preventDefault()}>
              <div
                className="backdrop-blur-md rounded-lg px-6 py-4 shadow-xl w-full border border-white/20"
                style={{ backgroundColor: 'rgba(0, 0, 0, 0.21)' }}
              >
                <label
                  htmlFor="shop-search"
                  className="block text-xs uppercase tracking-widest text-white mb-3 text-left font-medium"
                >
                  What are you shopping for?
                </label>
                <div className="flex items-center gap-4">
                  <input
                    id="shop-search"
                    type="text"
                    placeholder="Search body types or states (e.g. SUVs, California)..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="flex-1 text-base text-white placeholder:text-white/70 bg-transparent border-none outline-none focus:outline-none"
                  />
                  <button
                    type="submit"
                    className="text-white hover:text-white/80 transition-colors p-2 hover:bg-white/10 rounded-md"
                    aria-label="Search"
                  >
                    <Search className="w-6 h-6" />
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>

      <section className="py-section-lg px-6 bg-white">
        <div className="max-w-7xl mx-auto space-y-16">
          {/* Collections */}
          <div>
            <div className="mb-10">
              <p className="text-sm font-medium tracking-widest text-muted-navy uppercase mb-2">
                Collections
              </p>
              <p className="text-muted-navy">
                Shop by body type.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredBodyTypes.slice(0, 12).map((bt) => (
                <Link
                  key={bt.slug}
                  to={`/shop/${bt.slug}`}
                  className="group relative bg-white rounded-2xl overflow-hidden border border-line-gray hover:border-navy-900/20 transition-all duration-300 hover:shadow-xl"
                >
                  <div className="aspect-[4/3] bg-gradient-to-br from-gray-100 to-gray-200 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 via-navy-900/20 to-transparent" />
                    <CardImage
                      webpSrc={`/shop-images/body-types/${bt.slug}.webp`}
                      jpgSrc={`/shop-images/body-types/${bt.slug}.jpg`}
                      fallback={bt.pluralName.charAt(0)}
                    />
                    <div className="absolute top-4 right-4">
                      <Badge
                        variant="secondary"
                        className="bg-white/90 text-navy-900 backdrop-blur-sm"
                      >
                        {bt.vehicleCount.toLocaleString()} vehicles
                      </Badge>
                    </div>
                  </div>

                  <div className="p-6">
                    <h3 className="font-headline text-2xl font-light text-navy-900 group-hover:text-primary-600 transition-colors capitalize">
                      {bt.pluralName}
                    </h3>
                    <p className="mt-1 text-sm font-medium tracking-wide text-muted-navy uppercase">
                      {bt.tagline}
                    </p>
                    <p className="mt-4 text-muted-navy text-sm leading-relaxed line-clamp-3">
                      Browse {bt.vehicleCount.toLocaleString()} {bt.pluralName.toLowerCase()} currently available.
                    </p>

                    <div className="mt-6 flex items-center text-sm font-medium text-navy-900 group-hover:text-primary-600 transition-colors">
                      <span>Explore</span>
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <div className="mt-10 text-center">
              <Link to="/shop">
                <Button variant="outline">
                  Explore all collections <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Regional */}
          <div>
            <div className="mb-10">
              <p className="text-sm font-medium tracking-widest text-muted-navy uppercase mb-2">
                Regional
              </p>
              <p className="text-muted-navy">
                Shop by state. 50 states + DC, plus metro/city landing pages.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredStates.slice(0, 12).map((s) => (
                <Link
                  key={s.slug}
                  to={`/shop/${s.slug}`}
                  className="group relative bg-white rounded-2xl overflow-hidden border border-line-gray hover:border-navy-900/20 transition-all duration-300 hover:shadow-xl"
                >
                  <div className="aspect-[4/3] bg-gradient-to-br from-gray-100 to-gray-200 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 via-navy-900/20 to-transparent" />
                    <CardImage
                      webpSrc={`/shop-images/states/${s.slug}.webp`}
                      jpgSrc={`/shop-images/states/${s.slug}.jpg`}
                      fallback={s.abbreviation}
                    />
                    <div className="absolute top-4 right-4">
                      <Badge
                        variant="secondary"
                        className="bg-white/90 text-navy-900 backdrop-blur-sm"
                      >
                        {s.vehicleCount.toLocaleString()} vehicles
                      </Badge>
                    </div>
                  </div>

                  <div className="p-6">
                    <h3 className="font-headline text-2xl font-light text-navy-900 group-hover:text-primary-600 transition-colors">
                      {s.name}
                    </h3>
                    <p className="mt-1 text-sm font-medium tracking-wide text-muted-navy uppercase">
                      {s.cities.length} city pages
                    </p>
                    <p className="mt-4 text-muted-navy text-sm leading-relaxed line-clamp-3">
                      Explore inventory across {s.name} and nearby metro areas.
                    </p>

                    <div className="mt-6 flex items-center text-sm font-medium text-navy-900 group-hover:text-primary-600 transition-colors">
                      <span>Explore</span>
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <div className="mt-10 text-center flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to={`/shop/${statesWithCities[0]?.slug ?? ''}`}>
                <Button>
                  Explore a state <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/brands/compare">
                <Button variant="outline">Brand comparisons</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

